/* @flow */
import React, { Component } from 'react';
import {
  graphql,
  createRefetchContainer,
  type RelayRefetchProp,
} from 'react-relay';
import { CSVLink } from 'react-csv';
import { Field, formValueSelector } from 'redux-form';
import { TableRow, TableRowColumn } from 'material-ui/Table';
import { TextField } from 'redux-form-material-ui';
import RaisedButton from 'material-ui/RaisedButton';
import moment from 'moment';

import { Grid, Loader } from '../../components';
import type { PaymentExport_admin } from './__generated__/PaymentExport_admin.graphql';
import enhancer from '../enhancer';

type Props = {
  startDate: Date,
  endDate: Date,
  admin: PaymentExport_admin,
} & RelayRefetchProp;

type State = {
  error: ?Error,
  loading: boolean,
};

const styles = {
  header: {
    padding: 20,
  },
};

const defaultStartDate = moment()
  .startOf('month')
  .format()
  .split('T')[0];
const defaultEndDate = moment()
  .endOf('month')
  .format()
  .split('T')[0];

class PaymentExport extends Component<Props, State> {
  constructor(props) {
    super(props);

    // $FlowFixMe
    this.props.initialize({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    });

    this.state = { error: null, loading: false };
  }

  onSubmit = ({ startDate, endDate }) => {
    this.setState({ loading: true, error: null });

    this.props.relay.refetch(
      {
        startDate,
        endDate,
      },
      null,
      error => {
        this.setState({ error, loading: false });
      }
    );
  };

  renderRow = payment => (
    <TableRow key={payment.acceptedJobOfferId}>
      <TableRowColumn>{payment.acceptedJobOfferId}</TableRowColumn>
      <TableRowColumn>{payment.merchantReference}</TableRowColumn>
      <TableRowColumn>{payment.jobStatus}</TableRowColumn>
      <TableRowColumn>{payment.jobSessionStatus}</TableRowColumn>
      <TableRowColumn>{payment.paymentStatus}</TableRowColumn>
      <TableRowColumn>{payment.jobCreatedDate}</TableRowColumn>
      <TableRowColumn>{payment.jobStartDate}</TableRowColumn>
      <TableRowColumn>{payment.jobDuration}</TableRowColumn>
      <TableRowColumn>{payment.requestType}</TableRowColumn>
      <TableRowColumn>{payment.paymentStartedDate}</TableRowColumn>
      <TableRowColumn>{payment.paymentProviderAmount}</TableRowColumn>
      <TableRowColumn>{payment.paymentStudent}</TableRowColumn>
      <TableRowColumn>{payment.paymentTutor}</TableRowColumn>
      <TableRowColumn>{payment.paymentInternal}</TableRowColumn>
      <TableRowColumn>{payment.paymentExternal}</TableRowColumn>
      <TableRowColumn>{payment.studentName}</TableRowColumn>
      <TableRowColumn>{payment.studentCell}</TableRowColumn>
      <TableRowColumn>{payment.tutorName}</TableRowColumn>
      <TableRowColumn>{payment.tutorCell}</TableRowColumn>
      <TableRowColumn>{payment.tutorAccountHolder}</TableRowColumn>
      <TableRowColumn>{payment.tutorAccountNumber}</TableRowColumn>
      <TableRowColumn>{payment.tutorBank}</TableRowColumn>
      <TableRowColumn>{payment.tutorBranchCode}</TableRowColumn>
      <TableRowColumn>{payment.tutoringCompanyName}</TableRowColumn>
      <TableRowColumn>{payment.tutoringCompanyCode}</TableRowColumn>
    </TableRow>
  );

  render() {
    const { admin: { payments }, handleSubmit } = this.props;
    
    // Remove hashes ... new browser does not allow # in csv values
    for (var i = 0 ; i < payments.length; i++) {
      var payment = payments[i];
      payment["studentCell"] = payment["studentCell"].replace("#", "");
      payment["tutorAccountNumber"] = payment["tutorAccountNumber"].replace("#", "");
      payment["tutorBranchCode"] = payment["tutorBranchCode"].replace("#", "");
      payment["tutorCell"] = payment["tutorCell"].replace("#", "");
    }
    
    const { loading, error } = this.state;

    const headers = [
      'Job Offer',
      'Merch Ref',
      'Job Status',
      'Job Session Status',
      'Payment Status',
      'Job Created',
      'Job Start',
      'Job Duration',
      'Request Type',
      'Payment Started',
      'Provider Payment Amount',
      'Payment Student',
      'Payment Tutor',
      'Payment Internal',
      'Payment External',
      'Student Name',
      'Student Cell',
      'Tutor',
      'Tutor Cell',
      'Tutor Acc Holder',
      'Tutor Acc No',
      'Tutor Bank',
      'Tutor Branch Code',
      'Tutoring Compnany Name',
      'Tutoring Company Code',
    ];

    return (
      <div>
        <form style={styles.header}>
          <Field
            name="startDate"
            component={TextField}
            type="date"
            floatingLabelText="Start Date"
          />
          <Field
            name="endDate"
            component={TextField}
            type="date"
            floatingLabelText="End Date"
          />
          <RaisedButton
            label="Search"
            primary
            onClick={handleSubmit(this.onSubmit)}
          />
          {loading ? <Loader /> : null}
          {error && <div>{error.message}</div>}

          <div style={{ overflowY: 'scroll' }}>
            <div style={{ minWidth: '3200px' }}>
              <Grid
                headers={headers}
                renderRow={this.renderRow}
                rows={payments}
              />
            </div>
          </div>
          <CSVLink data={payments}>Export</CSVLink>
        </form>
      </div>
    );
  }
}

const selector = formValueSelector('paymentExportForm');

const query = graphql`
  query PaymentExportAdminQuery($startDate: Date!, $endDate: Date!) {
    admin {
      ...PaymentExport_admin
    }
  }
`;

export default enhancer(PaymentExport, {
  form: {
    form: 'paymentExportForm',
  },
  mapStateToProps: state => ({
    filterValues: selector(state, 'startDate', 'endDate'),
  }),
  relay: {
    query,
    variables: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
    create: () =>
      createRefetchContainer(
        PaymentExport,
        {
          admin: graphql`
            fragment PaymentExport_admin on Admin {
              payments(startDate: $startDate, endDate: $endDate) {
                jobId
                jobSessionId
                acceptedJobOfferId
                merchantReference
                jobStatus
                jobSessionStatus
                paymentStatus
                jobCreatedDate
                jobStartDate
                jobDuration
                requestType
                paymentStartedDate
                paymentProviderAmount
                paymentStudent
                paymentTutor
                paymentInternal
                paymentExternal
                studentName
                studentCell
                tutorName
                tutorCell
                tutorAccountHolder
                tutorAccountNumber
                tutorBank
                tutorBranchCode
                tutoringCompanyName
                tutoringCompanyCode
              }
            }
          `,
        },
        query
      ),
  },
});



// WEBPACK FOOTER //
// ./src/modules/financial/PaymentExport.js